import { getAccessToken } from './spotifyAuth';

const BASE_URL = 'https://api.spotify.com/v1';

// Helper function to make authenticated requests
const fetchSpotify = async (endpoint: string, options: RequestInit = {}): Promise<any> => {
  const token = await getAccessToken();
  
  if (!token) {
    throw new Error('No valid access token');
  }

  const response = await fetch(`${BASE_URL}${endpoint}`, {
    ...options,
    headers: {
      ...options.headers,
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json',
    },
  });

  if (!response.ok) {
    const errorBody = await response.text();
    console.error(`Spotify API Error [${response.status}] ${endpoint}:`, errorBody);
    throw new Error(`Spotify API error: ${response.status} ${endpoint}`);
  }

  return await response.json();
};

// Get current user profile
export const getCurrentUser = async () => {
  return await fetchSpotify('/me');
};

// Get user's top artists
export const getTopArtists = async (timeRange: 'short_term' | 'medium_term' | 'long_term' = 'long_term', limit: number = 50) => {
  return await fetchSpotify(`/me/top/artists?time_range=${timeRange}&limit=${limit}`);
};

// Get user's top tracks
export const getTopTracks = async (timeRange: 'short_term' | 'medium_term' | 'long_term' = 'long_term', limit: number = 50) => {
  return await fetchSpotify(`/me/top/tracks?time_range=${timeRange}&limit=${limit}`);
};

// Get recently played tracks
export const getRecentlyPlayed = async (limit: number = 50) => {
  return await fetchSpotify(`/me/player/recently-played?limit=${limit}`);
};

// Get multiple artists (for genre data)
export const getArtists = async (artistIds: string[]) => {
  const ids = artistIds.join(',');
  return await fetchSpotify(`/artists?ids=${ids}`);
};

// Get audio features for tracks
export const getAudioFeatures = async (trackIds: string[]) => {
  const ids = trackIds.join(',');
  return await fetchSpotify(`/audio-features?ids=${ids}`);
};

// Follow an artist
export const followArtist = async (artistId: string) => {
  return await fetchSpotify('/me/following?type=artist', {
    method: 'PUT',
    body: JSON.stringify({
      ids: [artistId]
    }),
  });
};

// Unfollow an artist
export const unfollowArtist = async (artistId: string) => {
  return await fetchSpotify('/me/following?type=artist', {
    method: 'DELETE',
    body: JSON.stringify({
      ids: [artistId]
    }),
  });
};

// Check if following artists
export const checkFollowingArtists = async (artistIds: string[]) => {
  const ids = artistIds.join(',');
  return await fetchSpotify(`/me/following/contains?type=artist&ids=${ids}`);
};

// Create a playlist
export const createPlaylist = async (userId: string, name: string, description: string, isPublic: boolean = true) => {
  return await fetchSpotify(`/users/${userId}/playlists`, {
    method: 'POST',
    body: JSON.stringify({
      name,
      description,
      public: isPublic,
    }),
  });
};

// Add tracks to playlist
export const addTracksToPlaylist = async (playlistId: string, trackUris: string[]) => {
  return await fetchSpotify(`/playlists/${playlistId}/tracks`, {
    method: 'POST',
    body: JSON.stringify({
      uris: trackUris,
    }),
  });
};

// Get saved tracks
export const getSavedTracks = async (limit: number = 50, offset: number = 0) => {
  return await fetchSpotify(`/me/tracks?limit=${limit}&offset=${offset}`);
};

// Fetch comprehensive listening data
export const fetchUserListeningData = async () => {
  try {
    // Fetch multiple time ranges in parallel
    const [
      user,
      topArtistsLongTerm,
      topArtistsMediumTerm,
      topArtistsShortTerm,
      topTracksLongTerm,
      topTracksMediumTerm,
      topTracksShortTerm,
      recentlyPlayed,
    ] = await Promise.all([
      getCurrentUser(),
      getTopArtists('long_term', 50),
      getTopArtists('medium_term', 50),
      getTopArtists('short_term', 50),
      getTopTracks('long_term', 50),
      getTopTracks('medium_term', 50),
      getTopTracks('short_term', 50),
      getRecentlyPlayed(50),
    ]);

    // Get audio features for top tracks (optional - requires Extended Quota Mode)
    let audioFeatures = null;
    try {
      const topTrackIds = topTracksLongTerm.items.map((track: any) => track.id);
      audioFeatures = await getAudioFeatures(topTrackIds);
    } catch (error) {
      console.warn('Audio features unavailable: Your Spotify app needs Extended Quota Mode enabled in the Developer Dashboard:', error);
    }

    // Get artist details for genres
    const topArtistIds = topArtistsLongTerm.items.slice(0, 20).map((artist: any) => artist.id);
    const artistDetails = await getArtists(topArtistIds);

    return {
      user,
      topArtists: {
        longTerm: topArtistsLongTerm.items,
        mediumTerm: topArtistsMediumTerm.items,
        shortTerm: topArtistsShortTerm.items,
      },
      topTracks: {
        longTerm: topTracksLongTerm.items,
        mediumTerm: topTracksMediumTerm.items,
        shortTerm: topTracksShortTerm.items,
      },
      recentlyPlayed: recentlyPlayed.items,
      audioFeatures: audioFeatures?.audio_features || [],
      artistDetails: artistDetails.artists,
    };
  } catch (error) {
    console.error('Error fetching Spotify data:', error);
    throw error;
  }
};

// Transform Spotify API data to match our existing format
export const transformSpotifyData = (spotifyData: any) => {
  const { topArtists, topTracks, recentlyPlayed, audioFeatures, artistDetails, user } = spotifyData;

  // Calculate total listening time (Spotify doesn't provide exact ms, so we estimate)
  const estimatedTotalMs = recentlyPlayed.length * 3 * 60 * 1000; // 3 min average per track

  // Extract genres from artists
  const allGenres: Record<string, number> = {};
  artistDetails.forEach((artist: any) => {
    artist.genres.forEach((genre: string) => {
      allGenres[genre] = (allGenres[genre] || 0) + 1;
    });
  });

  const topGenres = Object.entries(allGenres)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10)
    .map(([genre, count]) => ({ genre, count }));

  // Calculate audio profile from audio features (if available)
  let audioProfile = {
    danceability: '0',
    energy: '0',
    valence: '0',
    acousticness: '0',
    instrumentalness: '0',
    avgTempo: '0',
  };

  if (audioFeatures && audioFeatures.length > 0) {
    const avgAudioFeatures = audioFeatures.reduce((acc: any, feature: any) => {
      if (!feature) return acc;
      return {
        danceability: acc.danceability + (feature.danceability || 0),
        energy: acc.energy + (feature.energy || 0),
        valence: acc.valence + (feature.valence || 0),
        acousticness: acc.acousticness + (feature.acousticness || 0),
        instrumentalness: acc.instrumentalness + (feature.instrumentalness || 0),
        tempo: acc.tempo + (feature.tempo || 0),
        count: acc.count + 1,
      };
    }, { danceability: 0, energy: 0, valence: 0, acousticness: 0, instrumentalness: 0, tempo: 0, count: 0 });

    if (avgAudioFeatures.count > 0) {
      audioProfile = {
        danceability: ((avgAudioFeatures.danceability / avgAudioFeatures.count) * 100).toFixed(0),
        energy: ((avgAudioFeatures.energy / avgAudioFeatures.count) * 100).toFixed(0),
        valence: ((avgAudioFeatures.valence / avgAudioFeatures.count) * 100).toFixed(0),
        acousticness: ((avgAudioFeatures.acousticness / avgAudioFeatures.count) * 100).toFixed(0),
        instrumentalness: ((avgAudioFeatures.instrumentalness / avgAudioFeatures.count) * 100).toFixed(0),
        avgTempo: (avgAudioFeatures.tempo / avgAudioFeatures.count).toFixed(0),
      };
    }
  }

  // Transform to match the UI's expected format
  return {
    topArtists: topArtists.longTerm.map((artist: any, idx: number) => ({
      name: artist.name,
      ms: estimatedTotalMs / topArtists.longTerm.length, // Estimate playtime
      rank: idx + 1,
      id: artist.id,
      popularity: artist.popularity,
      genres: artist.genres,
      image: artist.images[0]?.url,
      followers: artist.followers.total,
      externalUrl: artist.external_urls.spotify,
    })),
    topTracks: topTracks.longTerm.map((track: any, idx: number) => ({
      name: track.name,
      ms: track.duration_ms, // Use track duration
      count: 1, // Spotify API doesn't provide play count
      artist: track.artists.map((a: any) => a.name).join(', '),
      artistName: track.artists.map((a: any) => a.name).join(', '),
      rank: idx + 1,
      id: track.id,
      artistId: track.artists[0]?.id,
      albumName: track.album.name,
      image: track.album.images[0]?.url,
      previewUrl: track.preview_url,
      duration: track.duration_ms,
      popularity: track.popularity,
      externalUrl: track.external_urls.spotify,
      uri: track.uri,
    })),
    topAlbums: [], // Spotify doesn't have top albums endpoint
    allArtistsList: topArtists.longTerm.map((artist: any, idx: number) => ({
      name: artist.name,
      count: idx + 1,
    })),
    allTracksList: topTracks.longTerm.map((track: any, idx: number) => ({
      name: track.name,
      artistName: track.artists.map((a: any) => a.name).join(', '),
      count: idx + 1,
    })),
    allAlbumsList: [],
    totalHours: Math.round(estimatedTotalMs / 3600000),
    totalTracks: topTracks.longTerm.length,
    totalArtists: topArtists.longTerm.length,
    uniqueArtists: topArtists.longTerm.length,
    totalListeningTimeMs: estimatedTotalMs,
    maxStreak: 1, // Spotify doesn't provide streak data
    singlePlayTracks: 0, // Spotify doesn't provide this
    firstTrack: recentlyPlayed.length > 0 ? {
      name: recentlyPlayed[recentlyPlayed.length - 1].track.name,
      artist: recentlyPlayed[recentlyPlayed.length - 1].track.artists.map((a: any) => a.name).join(', '),
      ts: recentlyPlayed[recentlyPlayed.length - 1].played_at,
    } : null,
    lastTrack: recentlyPlayed.length > 0 ? {
      name: recentlyPlayed[0].track.name,
      artist: recentlyPlayed[0].track.artists.map((a: any) => a.name).join(', '),
      ts: recentlyPlayed[0].played_at,
    } : null,
    mostPlayedTrack: topTracks.longTerm.length > 0 ? {
      name: topTracks.longTerm[0].name,
      count: 1,
    } : null,
    longestTrack: topTracks.longTerm.reduce((longest: any, track: any) => {
      return track.duration_ms > (longest?.ms || 0) ? { name: track.name, ms: track.duration_ms } : longest;
    }, null),
    shortestTrack: topTracks.longTerm.reduce((shortest: any, track: any) => {
      return track.duration_ms < (shortest?.ms || Infinity) ? { name: track.name, ms: track.duration_ms } : shortest;
    }, null),
    sessionMax: {
      duration: 0,
      start: null,
      end: null,
    },
    // Data for Timeline/Patterns/Insights tabs (limited without detailed history)
    monthlyData: [],
    hourlyDistribution: Array(24).fill(0),
    dayOfWeekDistribution: {
      Monday: 0,
      Tuesday: 0,
      Wednesday: 0,
      Thursday: 0,
      Friday: 0,
      Saturday: 0,
      Sunday: 0,
    },
    // Advanced analytics placeholders
    topSkippedTracks: [],
    artistBinges: [],
    listeningVelocity: [],
    genreEvolution: [],
    // Flag to indicate Spotify mode
    isSpotifyMode: true,
    // Spotify-specific data
    spotifyData: {
      user,
      topGenres,
      audioProfile,
      recentlyPlayed: recentlyPlayed.map((item: any) => ({
        track: item.track.name,
        artist: item.track.artists.map((a: any) => a.name).join(', '),
        playedAt: item.played_at,
        previewUrl: item.track.preview_url,
      })),
    },
  };
};
